import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Link, NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// Material
import { makeStyles } from "@material-ui/core/styles";
import CardActionArea from "@material-ui/core/CardActionArea";
import Collapse from "@material-ui/core/Collapse";
import AccordionSummary from "@material-ui/core/AccordionSummary";

// Config
import { getRoute, urls } from "../../config/routes";
import colors from "../../config/colors";
import styles from "../../config/styles";
import useTranslate from "../../hooks/useTranslate";

// Context
import { panelNames, useCaseSidePanel } from "../../context/CaseSidePanelProvider";

// Actions
import { getPhysicalDamages } from "../../state/physicalDamage/actions";

// Images
import chevron from "../../assets/icons/chevron.svg";

// Components
import Button from "../Button";
import PhysicalDamagePlaceholder from "./PhysicalDamagePlaceholder";
import PhysicalDamageListItem from "./PhysicalDamageListItem";
import Text from "../Text";

const useStyles = makeStyles(theme => ({
  root: {
    borderBottom: styles.values.border
  },
  title: {
    color: colors.cloudBurst,
    textTransform: "capitalize",
    padding: theme.spacing(1, 0)
  },
  injuries: {
    display: "flex",
    flexDirection: "column"
  },
  injuryItem: {
    padding: theme.spacing(2),
    position: "relative"
  },
  injuryActive: {
    "&:after": {
      content: `""`,
      backgroundColor: colors.dodgerBlue,
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      opacity: 0.1
    }
  },
  injuryType: {
    marginBottom: theme.spacing(1)
  },
  iconButton: {
    fontSize: 14,
    transform: ({ expanded }) => !expanded && "rotate(180deg)"
  },
  buttonAdd: {
    display: "flex",
    alignSelf: "flex-end",
    width: "auto",
    textTransform: "uppercase",
    fontWeight: "normal",
    padding: theme.spacing(1.5),
    color: colors.dodgerBlue,
    margin: theme.spacing(2)
  }
}));

const CollapsePhysicalDamage = ({ caseId }) => {
  const [expanded, toggleExpanded] = useCaseSidePanel(
    panelNames.PHYSICAL_DAMAGE
  );
  const classes = useStyles({ expanded });
  const getText = useTranslate();
  const dispatch = useDispatch();
  const { list } = useSelector(s => s.physicalDamage);

  // This component remounts on every case sub-page navigation (each page
  // mounts its own SectionWrapper), so fetching unconditionally on mount is
  // what keeps the list from going stale as the user navigates around.
  useEffect(() => {
    dispatch(getPhysicalDamages({ caseId }));
  }, [caseId, dispatch]);

  const damages = list || [];

  return (
    <div className={classes.root}>
      <AccordionSummary
        classes={{ expandIcon: classes.iconButton }}
        expandIcon={<img src={chevron} alt="" className={classes.chevron} />}
        onClick={toggleExpanded}
        size="small"
      >
        <Text
          className={classes.title}
          text="caseMenu.caseSummary.physicalDamage.title"
          type="valueLabel"
        />
      </AccordionSummary>
      <Collapse in={expanded} timeout="auto">
        {damages.length > 0 ? (
          <div className={classes.injuries}>
            {damages.map((damage, index) => (
              <CardActionArea
                component={NavLink}
                key={damage.id}
                to={getRoute(urls.physicalDamageDetail, {
                  caseId,
                  physicalDamageId: damage.id
                })}
                activeClassName={classes.injuryActive}
                className={classes.injuryItem}
              >
                <PhysicalDamageListItem
                  number={index + 1}
                  numberOfMediaItems={damage.numberOfMediaItems}
                />
              </CardActionArea>
            ))}
            <Button
              className={classes.buttonAdd}
              color="primary"
              component={Link}
              disableElevation
              regular
              to={getRoute(urls.addPhysicalDamage, { caseId })}
            >
              {getText("physicalDamagePage.addNewDamageButton")}
            </Button>
          </div>
        ) : (
          <PhysicalDamagePlaceholder
            text={getText("physicalDamagePage.addDamageButton")}
          />
        )}
      </Collapse>
    </div>
  );
};

CollapsePhysicalDamage.propTypes = {
  caseId: PropTypes.string
};

CollapsePhysicalDamage.defaultProps = {
  caseId: ""
};

export default CollapsePhysicalDamage;
